import React, { useCallback, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import { Box, CircularProgress } from '@material-ui/core'
import { CSVLink } from 'react-csv'
import clsx from 'clsx'
import { useQuery } from '@tanstack/react-query'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import { useColumns } from '../columnConfig'
import config from '../../../../config'
import { useAppContext } from '../../../../redux/slices/appContext'
import { ICON_NAMES, TEXT_VARIANTS } from '../../../../constants'
import Text from '../../../atoms/Text'
import Icon from '../../../atoms/Icon'
import { useResearchViewContext } from '../ResearchViewProvider'
import { useGetGroupedCoreData } from '../../../../api/coreData'
import SydButton from '../../../commonDesign/Button'
import PresentationTable from '../../PresentationTable'
import { postNamedQuery } from '../../../../service'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import SydModal from '../../../commonDesign/SydModal'
import useStyles from './styles'

const AccountDetailTable = ({ accountId }) => {
  const { firmId, isSummitUser } = useAppContext()
  const { formatter } = useFormattingContext()
  const { levelFilters, dateRange, exportColumns } = useResearchViewContext()

  const classes = useStyles()
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isPositionsExpanded, setIsPositionsExpanded] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportLevel, setExportLevel] = useState('account')
  const { columns, defaultSort } = useColumns({
    dataType: 'accountDetail',
    formatter
  })

  const mammothBaseUrl = useMemo(() => `${config.mammothUiUrl}/batchData/${firmId}`, [firmId])
  const onRowClick = useCallback((row) => {
    if (!isSummitUser) return null
    const { original: { accountId } } = row
    const valueDate = dayjs.utc(row.original.maxDate).format('YYYY-MM-DD')
    window.open(`${mammothBaseUrl}/${accountId}/${valueDate}`, '_blank')
  }, [isSummitUser, mammothBaseUrl])

  const downloadFileName = useMemo(() => {
    if (levelFilters.assetIds) {
      return `asset_${levelFilters.assetIds.join('_')}_account_${accountId}_details`
    }
    if (levelFilters?.accountIds?.length) {
      return `accounts_${levelFilters.accountIds.join('_')}_${accountId}_details`
    }
    return `account_${accountId}_details`
  }, [accountId, levelFilters.accountIds, levelFilters.assetIds])

  const { data, isLoading, error } = useQuery({
    queryKey: ['researchView', dateRange, levelFilters, accountId],
    queryFn: async () => {
      const [
        accountLevelData,
        assetLevelData
      ] = await Promise.all([
        postNamedQuery('coreData', 'getGroupedCoreData', {
          levelFilters: {
            ...levelFilters,
            accountIds: [accountId],
            levelTypes: ['account'],
            additionalData: {
              dataSources: ['name']
            }
          },
          dateRange
        }),
        postNamedQuery('coreData', 'getGroupedCoreData', {
          levelFilters: {
            ...levelFilters,
            accountIds: [accountId],
            levelTypes: ['account', 'asset'],
            additionalData: {
              dataSources: ['name']
            }
          },
          dateRange
        })
      ])

      const uniqueDataSources = [...new Set(
        assetLevelData.data.map(asset => asset.dataSourceId),
        accountLevelData.data.map(account => account.dataSourceId)
      )]

      const dataSources = await postNamedQuery('coreData', 'custodians', {
        filters: { dataSourceId: uniqueDataSources }
      })

      return {
        accountLevelData: accountLevelData.data.map(account => ({
          ...account,
          dataSourceName: dataSources.data.find(ds => ds.dataSourceId === account.dataSourceId)?.name
        })),
        assetLevelData: assetLevelData.data.map(asset => ({
          ...asset,
          dataSourceName: dataSources.data.find(ds => ds.dataSourceId === asset.dataSourceId)?.name
        }))
      }
    }
  })

  const tableData = useMemo(() => {
    if (!data) return []
    const { accountLevelData, assetLevelData } = data
    return accountLevelData.map((account) => {
      const assetValueDates = assetLevelData
        .filter((asset) => asset.maxDate === account.maxDate)
        .map(a => ({ ...a, _next: null, isAssetLevel: true }))
      return {
        ...account,
        expanded: isPositionsExpanded,
        subRows: assetValueDates
      }
    })
  }, [data, isPositionsExpanded])

  const csvData = useMemo(() => {
    if (!tableData?.length) return []
    const flattenedData = tableData.flatMap(({ subRows, ...account }) => (
      [{
        ...account,
        ...(exportLevel === 'position' ? ({
          assetId: null,
          Symbol: null,
          CUSIP: null,
          assetIdentifier: null,
          assetName: null,
          assetLongName: null,
          estimatedPrice: null
        }) : {})
      }, ...(exportLevel === 'position' ? subRows : [])]
    ))

    return flattenedData.reduce((acc, row) => {
      const record = exportColumns.reduce((record, { key, label, formatString, internalOnly }) => {
        const value = row[key]
        if (value === undefined || (internalOnly && !isSummitUser)) return record
        record[label] = formatString ? formatter(value, formatString) : value
        return record
      }, {})
      acc.push(record)
      return acc
    }, [])
  }, [tableData, exportColumns, isSummitUser, formatter, exportLevel])

  if (error) return <div> Error {JSON.stringify(error)} </div>

  return (
    <>
      <div className={classes.header}>
        <Text variant={TEXT_VARIANTS.h3}>
          Account <div className={classes.label}>{accountId}</div>
        </Text>
        <Box display='flex' gridGap={6}>
          {!!csvData?.length && (
            <SydButton variant='primary' icon={ICON_NAMES.download} size='sm' onClick={() => setShowExportModal(true)}>
              Download CSV
            </SydButton>
          )}
          <SydButton
            size='sm'
            onClick={() => setIsPositionsExpanded(!isPositionsExpanded)}
          >
            {isPositionsExpanded ? 'Hide Position Day Values' : 'Show Position Day Values'}
          </SydButton>
          <SydButton
            size='sm'
            className={clsx([classes.collapseButton], { [classes.collapseButtonActive]: !isCollapsed })}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <Icon name={ICON_NAMES.chevronDown} customSize={18} />
          </SydButton>
        </Box>
      </div>

      {!isCollapsed && (
        <PresentationTable
          key={`detailTable${accountId}`}
          columns={columns}
          data={tableData ?? []}
          loading={isLoading}
          defaultSort={defaultSort}
          onRowClick={onRowClick}
          sortable
        />
      )}

      <SydModal
        open={showExportModal}
        title={`Export Account ${accountId} Details`}
        subtitle='What levels would you like to export?'
        onClose={() => setShowExportModal(false)}
      >
        <Box display='flex' gridGap={12} mb={4}>
          <Box flexGrow={1}>
            <SydButton
              variant={exportLevel === 'account' ? 'primary' : 'secondary'}
              onClick={() => setExportLevel('account')}
              style={{ width: '100%' }}
            >
              Account Level Only
            </SydButton>
          </Box>

          <Box flexGrow={1}>
            <SydButton
              variant={exportLevel === 'position' ? 'primary' : 'secondary'}
              onClick={() => setExportLevel('position')}
              style={{ width: '100%' }}
            >
              Include Position Level Value
            </SydButton>
          </Box>
        </Box>

        <SydModalActions>
          <SydButton variant='secondary' onClick={() => setShowExportModal(false)}>
            Cancel
          </SydButton>
          <CSVLink data={csvData} filename={downloadFileName ?? `account_${accountId}_details`}>
            <SydButton variant='primary' icon={ICON_NAMES.download}>
              Download CSV
            </SydButton>
          </CSVLink>
        </SydModalActions>
      </SydModal>
    </>
  )
}

AccountDetailTable.propTypes = {
  accountId: PropTypes.number
}

const AccountDetailTables = () => {
  const { levelFilters, dateRange } = useResearchViewContext()
  const classes = useStyles()

  const { data: accounts, isLoading, error } = useGetGroupedCoreData({
    levelFilters: {
      ...levelFilters,
      levelTypes: ['account'],
      dateType: 'all'
    },
    dateRange
  })

  if (error) return <div> Error {JSON.stringify(error)} </div>
  if (isLoading) {
    return (
      <Box display='flex' flexDirection='center' justifyContent='center' alignItems='center' height='150px'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <div>
      {accounts?.length > 1 && (
        <div className={classes.sectionHeader}>
          <Text variant={TEXT_VARIANTS.h3}>
            {accounts?.length} Accounts
          </Text>
        </div>
      )}
      {accounts?.map(({ accountId }) => (
        <Box key={`accountDiv${accountId}`} mb={1}>
          <AccountDetailTable accountId={accountId} />
        </Box>
      ))}
    </div>
  )
}

export default AccountDetailTables
